import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { SITE_URL } from "@/config";
import { collections } from "@/mocks/collections";
import { stories } from "@/mocks/stories";
import { CollectionCard, SectionHead, StoryRow } from "@/components/feature/cards";

export default function CollectionPage() {
  const { id } = useParams<{ id: string }>();
  const collection = collections.find((c) => c.id === id);
  const others = collections.filter((c) => c.id !== id);

  if (!collection) {
    return (
      <div className="container-x flex flex-col items-center py-24 text-center">
        <p className="font-heading text-5xl font-black text-primary-500">404</p>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          没有这个专题合集
        </h1>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
        >
          <i className="ri-home-4-line" /> 返回首页
        </Link>
      </div>
    );
  }

  const list = stories.filter((s) => s.collectionIds.includes(collection.id));
  const featured = list[0];
  const rest = list.slice(1);

  const breadcrumbList = [
    { name: "首页", path: "/" },
    { name: "专题合集", path: "/collection/featured" },
    { name: collection.name, path: `/collection/${collection.id}` },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbList.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: collection.name,
      description: collection.desc,
      url: `${SITE_URL}/collection/${collection.id}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: collection.name,
      itemListElement: list.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.title,
        url: `${SITE_URL}/story/${s.slug}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`${collection.name}合集盘点 - 实时吃瓜 | 吃瓜日报`}
        description={collection.desc}
        keywords={`实时吃瓜,${collection.name},${collection.topic.join(",")}`}
        canonicalPath={`/collection/${collection.id}`}
        ogImage={collection.cover}
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-gradient-to-b from-background-100/70 to-background-50">
        <div className="container-x pt-6">
          <nav className="mb-6" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              {breadcrumbList.map((b, i) => (
                <li key={b.path} className="flex items-center gap-1.5">
                  {i > 0 ? <i className="ri-arrow-right-s-line text-foreground-300" /> : null}
                  {i === breadcrumbList.length - 1 ? (
                    <span className="text-foreground-700">{b.name}</span>
                  ) : (
                    <Link to={b.path} className="transition-colors hover:text-primary-600">
                      {b.name}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          <div className="flex flex-col gap-6 pb-8 lg:flex-row lg:items-center lg:gap-10 md:pb-10">
            <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-200 lg:w-[46%]">
              <img
                src={collection.cover}
                alt={collection.coverAlt}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex-1">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary-600">
                <i className="ri-folder-fill" /> 专题合集
              </p>
              <h1 className="mt-2 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                {collection.name}
              </h1>
              <p className="mt-1 text-sm font-bold text-primary-600">{collection.subtitle}</p>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-foreground-700">
                {collection.desc}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {collection.topic.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary-200 px-3 py-1 text-xs font-bold text-secondary-900"
                  >
                    <i className="ri-price-tag-3-line" /> {t}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1 rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-500">
                  <i className="ri-archive-2-line" /> 收录 {list.length} 篇
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* collection switcher */}
        <div className="border-t border-background-200 bg-background-50">
          <div className="container-x flex items-center gap-2 overflow-x-auto py-3 thin-scroll">
            <span className="mr-1 shrink-0 text-xs font-bold text-foreground-500">切换专题：</span>
            {collections.map((c) => (
              <Link
                key={c.id}
                to={`/collection/${c.id}`}
                className={
                  "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors " +
                  (c.id === collection.id
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600")
                }
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured + rest */}
      <section className="container-x py-10 md:py-14">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            {featured ? (
              <SectionHead kicker="本篇焦点" title="本专题精选" />
            ) : null}
            {featured ? (
              <Link
                to={`/story/${featured.slug}`}
                className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-background-200">
                  <img
                    src={featured.cover}
                    alt={featured.coverAlt}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-bold text-background-50">
                    精选 · {featured.tag}
                  </span>
                </div>
                <div className="p-5">
                  <h2 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {featured.title}
                  </h2>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                    {featured.lead}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary-600">
                    阅读全文 <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-6 text-center">
                <p className="text-sm text-foreground-600">
                  这个专题正在整理中，先去逛逛其他合集吧。
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 lg:col-span-8">
            <SectionHead kicker="合集内文章" title="全部收录" />
            {rest.map((s, i) => (
              <StoryRow key={s.slug} story={s} index={i + 2} />
            ))}
            {!featured || (featured && rest.length === 0) ? (
              <p className="py-6 text-sm text-foreground-600">
                {featured ? "更多内容整理中，欢迎回头再蹲。" : ""}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* Other collections */}
      <section className="border-t border-background-200 bg-background-100/60">
        <div className="container-x py-10 md:py-14">
          <SectionHead kicker="继续逛逛" title="更多专题合集" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((c) => (
              <CollectionCard key={c.id} collection={c} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}